import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import type { ThemeColours } from 'fym-shared/src/music24/releaseThemes';
import {
  PRESS_PHOTO_VARIANTS,
  PROFILE_BANNER_KINDS,
  profileImageSpec,
  type ArtistProfileKind,
} from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { Segmented } from '../../../components/primitives/Segmented';
import { useIsNarrowScreen } from '../../work/useIsNarrowScreen';
import type { HeaderPreviewGround } from '../../studio/spotifyHeader/FrameGround';
import { ProfileImageFrame } from '../frame/ProfileImageFrame';
import type { BannerTextPreview } from '../frame/BannerTextLayer';
import { pressRatioLabel } from '../frame/profileFrameGeometry';
import type { ProfileImageTool } from '../profileRoute';
import { ProfileToolSwitch } from './ProfileToolSwitch';
import { HeaderToolPreview } from './HeaderToolPreview';
import { profileToolMessages as t } from './profileToolMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfileToolPreviewProps {
  tool: ProfileImageTool;
  /** The ground of each kind that the preview draws (the banners: one per tab when they differ). */
  groundOf: (kind: ArtistProfileKind) => HeaderPreviewGround;
  colours: ThemeColours;
  /** Free and the trial: the Music24 mark where the render puts it. */
  watermark: boolean;
  artistName: string;
  avatarUrl: string | null;
  /** The banners: the text of the draft (design profile-card b). */
  bannerText?: BannerTextPreview | null;
}

const BANNER_LABELS = { youtubeBanner: t.youtubeBand, facebookCover: t.tabFacebook } as const;

/**
 * The live preview of a tool (design b1–b4, d2): the header with Desktop or Phone and the name and
 * the photo that Spotify draws; the two banners with their guides and the round photo of
 * Facebook; the artist photo round or square; the three press crops. The guides never go in the file.
 */
const ProfileToolPreviewComponent: React.FC<ProfileToolPreviewProps> = (props) => {
  const { tool, groundOf, colours, watermark, artistName, avatarUrl, bannerText = null } = props;
  const intl = useIntl();
  const narrow = useIsNarrowScreen();
  const [shape, setShape] = useState<'round' | 'square'>('round');
  const [guides, setGuides] = useState(true);
  const railWidth = narrow ? Math.min(320, window.innerWidth - 64) : 330;

  const guidesSwitch = (
    <ProfileToolSwitch
      id="profile-tool-guides"
      label={intl.formatMessage(t.cropGuides)}
      checked={guides}
      onChange={setGuides}
    />
  );

  if (tool === 'spotifyHeader') {
    return (
      <HeaderToolPreview
        ground={groundOf('spotifyHeader')}
        colours={colours}
        watermark={watermark}
        artistName={artistName}
        avatarUrl={avatarUrl}
        width={railWidth}
      />
    );
  }

  if (tool === 'spotifyAvatar') {
    return (
      <div className="d-flex flex-column gap-2" data-testid="profile-tool-preview">
        <Segmented
          ariaLabel={intl.formatMessage(t.previewTitle)}
          value={shape}
          onChange={setShape}
          options={[
            { value: 'round', label: intl.formatMessage(t.round) },
            { value: 'square', label: intl.formatMessage(t.square) },
          ]}
        />
        <div className={styles.railStage}>
          <ProfileImageFrame
            spec={profileImageSpec('spotifyAvatar')}
            width={220}
            ground={groundOf('spotifyAvatar')}
            colours={colours}
            round={shape === 'round'}
            watermark={watermark}
            testId="profile-avatar-preview"
          />
        </div>
      </div>
    );
  }

  const isBanners = tool === 'banners';
  return (
    <div className="d-flex flex-column gap-2" data-testid="profile-tool-preview">
      <div className={styles.railStage}>
        {isBanners
          ? PROFILE_BANNER_KINDS.map((kind) => (
              <div key={kind} className={styles.railCol}>
                <ProfileImageFrame
                  spec={profileImageSpec(kind)}
                  width={railWidth}
                  ground={groundOf(kind)}
                  colours={colours}
                  guides={guides}
                  sitePhotoUrl={kind === 'youtubeBanner' ? undefined : avatarUrl}
                  watermark={watermark}
                  bannerText={bannerText}
                  testId={`profile-preview-${kind}`}
                />
                <span className={styles.pressLabel}>
                  {intl.formatMessage(BANNER_LABELS[kind as keyof typeof BANNER_LABELS])}
                </span>
              </div>
            ))
          : PRESS_PHOTO_VARIANTS.map((variant) => (
              <div key={variant} className={styles.railCol}>
                <ProfileImageFrame
                  spec={profileImageSpec('pressPhoto', variant)}
                  width={{ landscape: 150, portrait: 96, square: 110 }[variant]}
                  ground={groundOf('pressPhoto')}
                  colours={colours}
                  guides={guides}
                  watermark={watermark}
                  testId={`profile-preview-press-${variant}`}
                />
                <span className={styles.pressLabel}>{pressRatioLabel(variant)}</span>
              </div>
            ))}
      </div>
      <div className={styles.toggles}>{guidesSwitch}</div>
    </div>
  );
};

export const ProfileToolPreview = TypedMemo(ProfileToolPreviewComponent);
